import React, { useState, useEffect } from 'react';
import { ShieldCheck, Plus, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

/**
 * Versiebeheer van de algemene voorwaarden, de verwerkersovereenkomst en het
 * privacybeleid. Een bestaande versie wordt nooit bijgewerkt -- een gewijzigde
 * tekst is een nieuwe versie. Alleen zo blijft aantoonbaar wat iemand destijds
 * aanvaard heeft.
 */
export const AdminLegalPage = () => {
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialoog, setDialoog] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [form, setForm] = useState({ doc_type: 'terms', version: '', content_markdown: '' });

  const laden = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getLegalDocuments();
      setDocs(data.documents || []);
      setTypes(data.types || {});
    } catch (e) {
      toast.error('Kon documenten niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { laden(); }, []);

  const publiceer = async () => {
    if (!form.version.trim() || !form.content_markdown.trim()) {
      toast.error('Vul een versienummer en een tekst in');
      return;
    }
    setBezig(true);
    try {
      const { data } = await adminAPI.publishLegalDocument(form);
      toast.success(data.message, { duration: 8000 });
      setDialoog(false);
      setForm({ doc_type: 'terms', version: '', content_markdown: '' });
      laden();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publiceren mislukt');
    } finally {
      setBezig(false);
    }
  };

  const datum = (v) => (v ? new Date(v).toLocaleDateString('nl-BE', {
    day: '2-digit', month: 'short', year: 'numeric',
  }) : '—');

  return (
    <div className="space-y-6" data-testid="admin-legal-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Voorwaarden &amp; privacy</h1>
          <p className="text-gray-400 mt-1">
            Publiceer een nieuwe versie en iedereen moet bij de volgende login opnieuw akkoord geven.
          </p>
        </div>
        <Button onClick={() => setDialoog(true)} className="btn-primary" data-testid="nieuwe-versie">
          <Plus className="w-4 h-4 mr-2" /> Nieuwe versie
        </Button>
      </div>

      <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-4 flex gap-3">
        <ShieldCheck className="w-5 h-5 text-blue-300 flex-shrink-0 mt-0.5" />
        <p className="text-blue-100 text-sm">
          Een gepubliceerde versie wordt nooit meer gewijzigd. Bij elke acceptatie wordt
          de SHA-256 van de tekst meegeschreven, zodat bij een geschil aantoonbaar is
          welke tekst iemand precies aanvaard heeft en wanneer.
        </p>
      </div>

      <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Document</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Versie</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Geldig sinds</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Acceptaties</th>
              <th className="px-4 py-4 text-left text-sm font-medium text-gray-300">Hash</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2" />Laden…
              </td></tr>
            )}
            {!loading && docs.map((d) => (
              <tr key={d.id} className={d.is_current ? '' : 'opacity-60'}>
                <td className="px-4 py-4">
                  <p className="text-white font-medium">{d.title}</p>
                  {d.is_placeholder && (
                    <span className="text-xs text-amber-400">voorlopige tekst</span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span className="text-white font-mono">{d.version}</span>
                  {d.is_current && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      actueel
                    </span>
                  )}
                </td>
                <td className="px-4 py-4 text-gray-300 text-sm">{datum(d.effective_from)}</td>
                <td className="px-4 py-4 text-gray-300 text-sm">{d.acceptances}</td>
                <td className="px-4 py-4 text-gray-500 font-mono text-xs">
                  {(d.content_hash || '').slice(0, 16)}…
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialoog} onOpenChange={setDialoog}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nieuwe versie publiceren</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">Document</Label>
                <select value={form.doc_type}
                        onChange={(e) => setForm({ ...form, doc_type: e.target.value })}
                        data-testid="legal-type"
                        className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 mt-1">
                  {Object.entries(types).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-gray-300">Versienummer</Label>
                <Input value={form.version} placeholder="2.0" data-testid="legal-versie"
                       onChange={(e) => setForm({ ...form, version: e.target.value })}
                       className="bg-gray-700 border-gray-600 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Tekst (Markdown)</Label>
              <textarea
                value={form.content_markdown}
                onChange={(e) => setForm({ ...form, content_markdown: e.target.value })}
                rows={14}
                data-testid="legal-tekst"
                placeholder="## Algemene voorwaarden&#10;&#10;### 1. ..."
                className="w-full bg-gray-900 border border-gray-600 text-gray-100 rounded-lg px-3 py-2 mt-1 font-mono text-sm"
              />
            </div>
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-amber-200 text-sm">
                Na publicatie moet iedereen die de vorige versie aanvaardde bij de
                eerstvolgende login opnieuw akkoord geven.
              </p>
            </div>
            <Button onClick={publiceer} disabled={bezig} className="btn-primary w-full h-11"
                    data-testid="legal-publiceer">
              {bezig ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Publiceren</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
